import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Button from "@/components/Button/Button";
import StatusBadge from "@/components/StatusBadge/StatusBadge";
import type { StatusTone } from "@/components/StatusBadge/StatusBadge";
import SimpleTable from "@/components/SimpleTable/SimpleTable";
import type { Column } from "@/types/common";
import ArrowIcon from "@/components/SVGComponents/ArrowIcon";
import DownloadIcon from "@/components/SVGComponents/DownloadIcon";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { getUsers } from "@/store/slices/userSlice";
import userService from "@/services/api/user";
import type { MemberSubscription, ParticipatedEvent, User } from "@/services/api/user";
import { EVENT_TYPE_LABELS, formatEventDate, formatTicketPrice } from "@/pages/Gatherings/lib/eventFormat";
import { formatDate } from "@/lib/dateFormat";
import { formatINR } from "@/lib/currency";
import { getDisplayName, getInitials } from "@/lib/user";
import { captalizeEachWord } from "@/lib/utils";
import { usePageSubtitle } from "@/hooks/usePageSubtitle";
import toast from "@/lib/toast";
import routes from "@/routes/routes";
import styles from "./MemberDetails.module.scss";

// Same real GET /users query base Members.tsx uses. No confirmed
// filter-by-id param exists (see services/api/user.ts), so this fetches a
// generous page and matches the real member by id client-side, same
// convention CreateGathering.tsx's edit-mode uses for a single event.
const MEMBER_QUERY_BASE = { usertype: "FOUNDER", screenType: "management" };
const MEMBERS_FETCH_PARAMS = { pageNo: 1, perPage: 100, ...MEMBER_QUERY_BASE };

const formatShortDate = (value: string) => formatDate(value);

const DEFAULT_TIER_LABEL = "Member";

interface ClassifiedStatus {
  label: string;
  tone: StatusTone;
}

// `ParticipatedEvent.status`'s real enum values are unknown (see
// services/api/user.ts) — classified by keyword rather than an exact
// lookup table, so an unanticipated real value still renders sensibly
// (raw, title-cased, neutral tone) instead of falling through to nothing.
const classifyAttendance = (status: string | null): ClassifiedStatus => {
  const normalized = (status || "").toUpperCase();
  if (normalized.includes("NO_SHOW") || normalized.includes("NOSHOW") || normalized.includes("MISS")) {
    return { label: "No-show", tone: "danger" };
  }
  if (normalized.includes("ATTEND") || normalized.includes("COMPLETE") || normalized.includes("CHECKED_IN")) {
    return { label: "Attended", tone: "success" };
  }
  if (!status || normalized.includes("CONFIRM") || normalized.includes("REGISTER") || normalized.includes("BOOK")) {
    return { label: "Confirmed", tone: "brand" };
  }
  return { label: status.replace(/_/g, " "), tone: "neutral" };
};

// `MemberSubscription.status`'s full set of real values is only confirmed
// as "ACTIVE" (the one real sample) — the rest is a keyword heuristic,
// same convention and same reasoning as `classifyAttendance` above, so an
// unanticipated real value (e.g. "CANCELLED", "EXPIRED") still renders
// sensibly instead of falling through to nothing.
const classifySubscriptionStatus = (status: string): ClassifiedStatus => {
  const normalized = status.toUpperCase();
  if (normalized.includes("ACTIVE")) return { label: status, tone: "success" };
  if (normalized.includes("CANCEL") || normalized.includes("EXPIR") || normalized.includes("FAIL")) {
    return { label: status, tone: "danger" };
  }
  if (normalized.includes("PAUSE") || normalized.includes("PEND") || normalized.includes("QUEUE")) {
    return { label: status, tone: "warning" };
  }
  return { label: status, tone: "neutral" };
};

const GATHERING_COLUMNS: Column<ParticipatedEvent>[] = [
  { name: "Gathering", cell: (row) => <span className={styles.gatheringName}>{row.name || "-"}</span> },
  { name: "Type", selector: (row) => EVENT_TYPE_LABELS[row.eventType] ?? row.eventType },
  { name: "Date", selector: (row) => formatEventDate(row.eventDate) },
  { name: "Ticket", selector: (row) => formatTicketPrice(row.ticketPrice) },
  {
    name: "Attendance",
    cell: (row) => {
      const { label, tone } = classifyAttendance(row.status);
      return <StatusBadge label={label.toUpperCase()} tone={tone} />;
    },
  },
];

const MemberDetails = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { memberId } = useParams<{ memberId: string }>();
  const numericMemberId = memberId ? Number(memberId) : NaN;

  const location = useLocation();
  const users = useAppSelector((state) => state.user.users);
  const isFetchingUsers = useAppSelector((state) => state.user.isFetching);
  const routedMember = location.state as User | undefined;
  const hasRoutedMember = Boolean(routedMember && String(routedMember.id) === memberId);
  const [hasRequestedUsers, setHasRequestedUsers] = useState(false);

  // Prefer the row passed from Members.tsx; otherwise (direct URL / page
  // refresh) fall back to finding it in the page-1 users fetch below.
  const member = useMemo(
    () => (hasRoutedMember ? routedMember : users.find((user) => String(user.id) === memberId)),
    [hasRoutedMember, routedMember, users, memberId],
  );

  useEffect(() => {
    if (hasRoutedMember) return;
    dispatch(getUsers(MEMBERS_FETCH_PARAMS));
    setHasRequestedUsers(true);
  }, [dispatch, hasRoutedMember]);

  const isLoadingMember = !member && (!hasRequestedUsers || isFetchingUsers);

  const [subscription, setSubscription] = useState<MemberSubscription | null>(null);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null);

  const fetchSubscription = useCallback(async () => {
    if (!Number.isFinite(numericMemberId)) return;
    setIsLoadingSubscription(true);
    setSubscriptionError(null);
    try {
      const result = await userService.getMemberSubscription(numericMemberId);
      setSubscription(result?.data ?? null);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        // A member with no active subscription is a real, expected state
        // for this endpoint, not a failure — leave `subscriptionError`
        // unset so the UI renders the "no active membership" branch below
        // instead of the error+retry one.
        setSubscription(null);
      } else {
        // privateRequest's response interceptor already shows an error
        // toast (see services/axios.ts) — this message is just for the
        // inline retry state below.
        setSubscriptionError("Couldn't load membership details.");
      }
    } finally {
      setIsLoadingSubscription(false);
    }
  }, [numericMemberId]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const [participatedEvents, setParticipatedEvents] = useState<ParticipatedEvent[]>([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [eventsError, setEventsError] = useState<string | null>(null);

  const fetchParticipatedEvents = useCallback(async () => {
    if (!Number.isFinite(numericMemberId)) return;
    setIsLoadingEvents(true);
    setEventsError(null);
    try {
      const result = await userService.getParticipatedEvents(numericMemberId);
      setParticipatedEvents(result?.data?.results ?? []);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        // Same reasoning as fetchSubscription above — no participated
        // events on file reads as an empty list, not a failure.
        setParticipatedEvents([]);
      } else {
        setEventsError("Couldn't load gathering history.");
      }
    } finally {
      setIsLoadingEvents(false);
    }
  }, [numericMemberId]);

  useEffect(() => {
    fetchParticipatedEvents();
  }, [fetchParticipatedEvents]);

  const attendedCount = participatedEvents.filter((row) => classifyAttendance(row.status).tone === "success").length;
  const noShowCount = participatedEvents.filter((row) => classifyAttendance(row.status).tone === "danger").length;

  const tierLabel = subscription?.name || DEFAULT_TIER_LABEL;
  // When the member isn't in the fetched page, use the subscriber name on
  // the subscription record rather than showing "Loading" forever.
  const subscriberName = [subscription?.firstName, subscription?.lastName].filter(Boolean).join(" ");
  const name = member
    ? getDisplayName(member)
    : isLoadingMember
      ? "Loading member…"
      : subscriberName || `Member #${memberId}`;
  const contactParts = member
    ? [member.email, member.mobileNo, member.companyName, member.sector, member.location || member.cityName].filter(
        Boolean,
      )
    : [];

  usePageSubtitle(member ? `${tierLabel} since ${formatShortDate(member.createdAt)}` : "");

  const goToMembers = () => navigate(routes.privateRoutes.MEMBERS.path);

  return (
    <div className={styles.page}>
      <button type="button" className={styles.backLink} onClick={goToMembers}>
        <ArrowIcon className={styles.backIcon} />
        Back to Members
      </button>

      <div className={styles.memberCard}>
        <div className={styles.avatar}>{isLoadingMember ? "" : getInitials(name)}</div>
        <div className={styles.memberInfo}>
          <div className={styles.nameRow}>
            <h1 className={styles.memberName}>{name}</h1>
            <StatusBadge label={tierLabel.toUpperCase()} tone="neutral" />
            {member && (
              <StatusBadge label={member.isActive ? "ACTIVE" : "INACTIVE"} tone={member.isActive ? "success" : "danger"} />
            )}
          </div>
          <p className={styles.contactLine}>{contactParts.length > 0 ? contactParts.join(" · ") : "—"}</p>
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h2 className={styles.cardTitle}>Membership</h2>
          {subscription &&
            (() => {
              const { label, tone } = classifySubscriptionStatus(subscription.status);
              return <StatusBadge label={label.toUpperCase()} tone={tone} />;
            })()}
        </div>

        {isLoadingSubscription ? (
          <p className={styles.stateText}>Loading membership details…</p>
        ) : subscriptionError ? (
          <div className={styles.stateText}>
            <p>{subscriptionError}</p>
            <span className={styles.buttonWrap}>
              <Button outlined onClick={fetchSubscription}>
                TRY AGAIN
              </Button>
            </span>
          </div>
        ) : !subscription ? (
          <p className={styles.stateText}>No active subscription for this member.</p>
        ) : (
          <>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Plan</span>
                <span className={styles.infoValue}>{subscription.name}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Price</span>
                <span className={styles.infoValue}>{formatINR(subscription.totalAmount)}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Started</span>
                <span className={styles.infoValue}>{formatShortDate(subscription.startDate)}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Expires</span>
                <span className={styles.infoValue}>{formatShortDate(subscription.endDate)}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Payment</span>
                <span className={styles.infoValue}>{subscription.paymentType ? captalizeEachWord(subscription.paymentType) : "—"}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Transaction ID</span>
                <span className={styles.infoValue}>{subscription.transactionId || "—"}</span>
              </div>
            </div>

            <div className={styles.actionsRow}>
              <span className={styles.buttonWrap}>
                <Button
                  outlined
                  startIcon={<DownloadIcon />}
                  onClick={() =>
                    subscription.invoiceUrl
                      ? window.open(subscription.invoiceUrl, "_blank", "noopener,noreferrer")
                      : toast.warning("No invoice is available for this subscription.")
                  }
                >
                  VIEW INVOICE
                </Button>
              </span>
            </div>
          </>
        )}
      </div>

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h2 className={styles.cardTitle}>Gathering History</h2>
          {!isLoadingEvents && !eventsError && (
            <span className={styles.attendanceSummary}>
              {attendedCount} attended · {noShowCount} no-shows
            </span>
          )}
        </div>
        {eventsError ? (
          <div className={styles.stateText}>
            <p>{eventsError}</p>
            <span className={styles.buttonWrap}>
              <Button outlined onClick={fetchParticipatedEvents}>
                TRY AGAIN
              </Button>
            </span>
          </div>
        ) : (
          <SimpleTable<ParticipatedEvent>
            columns={GATHERING_COLUMNS}
            data={participatedEvents}
            getRowKey={(row) => row.id}
            emptyMessage={isLoadingEvents ? "Loading gathering history…" : "No gatherings attended yet"}
          />
        )}
      </div>
    </div>
  );
};

export default MemberDetails;
